import React from 'react';
import './StockRecipeChoices.css';

export function initialStockChoice(product) {
  const config = product?.stockChoices;
  return config ? { schemaVersion: 1, configRevision: config.configRevision, variantId: null, modifierIds: [] } : null;
}
export function stockChoiceFor(product) {
  return product?.stockChoice || initialStockChoice(product);
}
export function stockChoiceValid(product) {
  const config = product?.stockChoices;
  if (!config) return true;
  const choice = stockChoiceFor(product);
  if (config.schemaVersion !== 1 || !Number.isInteger(config.configRevision) || config.configRevision < 1 || choice.schemaVersion !== 1 || choice.configRevision !== config.configRevision || !Array.isArray(choice.modifierIds)) return false;
  if (choice.variantId === null ? !config.allowBase : !config.variantsEnabled || !config.variants.some(v => v.id === choice.variantId)) return false;
  const selected = choice.modifierIds.map(id => config.modifiers.find(m => m.id === id));
  return selected.every(Boolean) && new Set(choice.modifierIds).size === selected.length && selected.every(m => m && config.modifiersEnabled && m.appliesTo.includes(choice.variantId || 'base') && !selected.some(other => other !== m && ((m.groupId && other.groupId === m.groupId) || m.conflictsWith.includes(other.id) || other.conflictsWith.includes(m.id))));
}
export function stockChoiceSignature(choice) {
  return choice ? JSON.stringify([choice.configRevision, choice.variantId, [...choice.modifierIds].sort()]) : '';
}
export function stockChoiceSummary(product) {
  const config = product?.stockChoices;
  const choice = stockChoiceFor(product);
  if (!config || !choice) return '';
  return [choice.variantId ? config.variants.find(v => v.id === choice.variantId)?.label : null, ...choice.modifierIds.map(id => config.modifiers.find(m => m.id === id)?.label)].filter(Boolean).join(' · ');
}
export function withInitialStockChoice(product) {
  return { ...product, stockChoice: initialStockChoice(product) };
}
export function menuStockSignature(selections) {
  return selections.map(selection => `${selection.sectionId}:${(selection.products || (selection.product ? [selection.product] : [])).map(p => `${p.id}:${stockChoiceSignature(stockChoiceFor(p))}`).sort().join(',')}`).sort().join('|');
}
export default function StockRecipeChoices({ product, onChange }) {
  const config = product?.stockChoices;
  if (!config) return null;
  const choice = stockChoiceFor(product);
  const changeVariant = variantId => onChange({ ...choice, variantId, modifierIds: choice.modifierIds.filter(id => config.modifiersEnabled && config.modifiers.some(m => m.id === id && m.appliesTo.includes(variantId || 'base'))) });
  return <fieldset className="stock-recipe-choices"><legend>Composition de {product.name}</legend>
    {config.variantsEnabled && <div className="stock-recipe-variants">
      {config.allowBase && <button type="button" aria-pressed={choice.variantId === null} onClick={() => changeVariant(null)}>Recette habituelle</button>}
      {config.variants.map(variant => <button type="button" key={variant.id} aria-pressed={choice.variantId === variant.id} onClick={() => changeVariant(variant.id)}>{variant.label}</button>)}
    </div>}
    {config.modifiersEnabled && config.modifiers.map(modifier => {
      const checked = choice.modifierIds.includes(modifier.id);
      const disabled = !modifier.appliesTo.includes(choice.variantId || 'base') || (!checked && choice.modifierIds.some(id => { const other = config.modifiers.find(m => m.id === id); return other && ((modifier.groupId && modifier.groupId === other.groupId) || modifier.conflictsWith.includes(id) || other.conflictsWith.includes(modifier.id)); }));
      return <label key={modifier.id}><input type="checkbox" checked={checked} disabled={disabled} onChange={() => onChange({ ...choice, modifierIds: (checked ? choice.modifierIds.filter(id => id !== modifier.id) : [...choice.modifierIds, modifier.id]).sort() })} />{modifier.label}{disabled && !checked ? ' (indisponible avec ce choix)' : ''}</label>;
    })}
    {!stockChoiceValid(product) && <p role="status">Choisissez une recette pour continuer.</p>}
    <small>Ces choix ne changent pas le prix. Les suppléments payants restent séparés.</small>
  </fieldset>;
}

export function stockRecipeError(payload, fallback) {
  const code = String(payload?.code || '');
  if (code === 'BESTOCK_RECIPE_SELECTION_INVALID') return 'Ces choix ne sont pas compatibles. Modifiez la composition avant de commander.';
  if (code === 'BESTOCK_RECIPE_SELECTION_REQUIRED') return 'Choisissez la recette de chaque produit avant de commander.';
  if (code === 'BESTOCK_RECIPE_CONFIGURATION_STALE') return 'La composition a changé. Actualisez le catalogue puis refaites vos choix avant de commander.';
  if (code.startsWith('BESTOCK_RECIPE_')) return 'La composition est invalide. Vérifiez vos choix avant de commander.';
  return payload?.error || payload?.message || fallback;
}

// Only for printing after order acceptance. Never send this display copy as a kitchen instruction.
export function stockRecipePrintCart(cart) {
  const displayProduct = product => {
    const summary = stockChoiceSummary(product);
    return summary ? { ...product, kitchenName: [product.kitchenName || product.name, summary].join(' · ') } : product;
  };
  return cart.map(item => item.isMenu ? { ...item, customSelections: (item.customSelections || []).map(selection => ({ ...selection, ...(selection.products ? { products: selection.products.map(displayProduct) } : {}), ...(selection.product ? { product: displayProduct(selection.product) } : {}) })) } : displayProduct(item));
}
